import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  InputSignal,
  Output,
  computed,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';

@Component({
  selector: 'stc-apps-editable-dropdown',
  standalone: true,
  imports: [CommonModule, DropdownModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditableDropdownComponent),
      multi: true,
    },
  ],
  templateUrl: './editable-dropdown.component.html',
  styleUrl: './editable-dropdown.component.scss',
})
export class EditableDropdownComponent implements ControlValueAccessor {
  // ───────── Inputs ─────────
  dropdownOptions: InputSignal<{ label: string; value: any }[]> = input<
    { label: string; value: any }[]
  >([]);
  placeholder = input<string>('');
  showClear = input<boolean>(true);
  panelStyleClass = input<string>('');
  class = input<string>('');
  appendTo = input<string | null>(null);

  // ───────── Outputs ─────────
  @Output() valueChange = new EventEmitter<any>();
  @Output() dropdownShow = new EventEmitter<void>();

  // ───────── State ─────────
  private formValue = signal<any>(null);
  searchText = signal<string>('');
  disabled = false;

  private selectedOption = computed(
    () =>
      this.dropdownOptions().find((o) => o.value === this.formValue()) ?? null
  );

  model = computed(() => this.searchText() || this.selectedOption());

  filteredOptions = computed(() => {
    const text = this.searchText().trim().toLowerCase();
    const all = this.dropdownOptions();
    return text ? all.filter((o) => o.label.toLowerCase().includes(text)) : all;
  });

  // ───────── CVA ─────────
  private onChange: (v: any) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(v: any): void {
    this.formValue.set(v ?? null);
    this.searchText.set('');
  }
  registerOnChange(fn: (v: any) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ───────── Handlers ─────────
  onModelChange(m: unknown): void {
    if (m === null || m === undefined || m === '') {
      this.searchText.set('');
      this.commit(null);
      return;
    }
    if (typeof m === 'string') {
      this.searchText.set(m);
      return;
    }
    this.searchText.set('');
    this.commit((m as { value: any }).value);
  }

  onShow(): void {
    this.dropdownShow.emit();
  }

  onHide(): void {
    this.searchText.set('');
    this.onTouched();
  }

  private commit(v: any): void {
    if (v === this.formValue()) return;
    this.formValue.set(v);
    this.onChange(v);
    this.valueChange.emit(v);
  }
}
